import type { ReactNode } from 'react'
import { useState } from 'react'
import { createPortal } from 'react-dom'

interface Props {
  titulo: string
  nota?: string
  alCerrar: () => void
  /**
   * El contenido. Si es una funcion recibe `cerrar`, que baja la hoja con su
   * animacion antes de avisar a `alCerrar`: para los botones que eligen algo
   * y cierran, que si llamasen a `alCerrar` directamente la hoja
   * desapareceria de golpe.
   */
  children: ReactNode | ((cerrar: () => void) => ReactNode)
}

// Lo mismo que la media query del CSS: sin animacion no llega el
// `animationend`, y la hoja se quedaria abierta esperandolo.
const sinMovimiento = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches

/**
 * Hoja que sube desde abajo, al estilo de iOS.
 *
 * Se pinta con un portal colgando de <body> y no donde se declara. Dentro de
 * `.contenido` —que tiene scroll propio— Safari en iOS se lia con el orden de
 * pintado de los elementos fijos y la barra de pestanas acababa dibujandose
 * por encima de la hoja: se veian los iconos flotando sobre los botones.
 *
 * Entra subiendo y sale bajando. La salida es cosa de la hoja: se marca como
 * `saliendo`, el CSS la baja y solo al terminar se avisa al padre, que es
 * quien la desmonta.
 */
export default function Hoja({ titulo, nota, alCerrar, children }: Props) {
  const [saliendo, setSaliendo] = useState(false)

  function cerrar() {
    if (saliendo) return
    if (sinMovimiento()) alCerrar()
    else setSaliendo(true)
  }

  return createPortal(
    <div className={saliendo ? 'hoja-fondo saliendo' : 'hoja-fondo'} onClick={cerrar}>
      <div
        className="hoja"
        onClick={(e) => e.stopPropagation()}
        onAnimationEnd={(e) => {
          if (saliendo && e.target === e.currentTarget) alCerrar()
        }}
      >
        <strong>{titulo}</strong>
        {nota && <p className="meta">{nota}</p>}
        {typeof children === 'function' ? children(cerrar) : children}
      </div>
    </div>,
    document.body,
  )
}
